/**
 * Codificador QR minimo (modo byte, nivel de correccion M).
 *
 * Existe para que el PDF de la factura pueda dibujar el QR de AFIP/ARCA sin
 * sumar una dependencia nueva al bundle. Cubre solo lo que la factura necesita:
 * un unico segmento en modo byte, mascara elegida por penalizacion estandar y
 * versiones 1 a 20 (la URL del QR de AFIP entra holgada en ese rango).
 *
 * La salida es una matriz booleana: true = modulo oscuro.
 */

/* ------------------------------------------------------------ campo GF(256) */

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);

(() => {
    let x = 1;
    for (let i = 0; i < 255; i++) {
        EXP[i] = x;
        LOG[x] = i;
        x <<= 1;
        if (x & 0x100) x ^= 0x11d; // polinomio generador del campo
    }
    for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255];
})();

const gfMul = (a: number, b: number) => (a === 0 || b === 0 ? 0 : EXP[LOG[a] + LOG[b]]);

/** Polinomio generador de grado `degree` para Reed-Solomon. */
const rsGenerator = (degree: number): Uint8Array => {
    let poly = new Uint8Array([1]);
    for (let i = 0; i < degree; i++) {
        const next = new Uint8Array(poly.length + 1);
        for (let j = 0; j < poly.length; j++) {
            next[j] ^= poly[j];
            next[j + 1] ^= gfMul(poly[j], EXP[i]);
        }
        poly = next;
    }
    return poly;
};

const rsEncode = (data: Uint8Array, ecLength: number): Uint8Array => {
    const gen = rsGenerator(ecLength);
    const result = new Uint8Array(ecLength);
    for (let i = 0; i < data.length; i++) {
        const factor = data[i] ^ result[0];
        result.copyWithin(0, 1);
        result[ecLength - 1] = 0;
        for (let j = 0; j < ecLength; j++) result[j] ^= gfMul(gen[j + 1], factor);
    }
    return result;
};

/* ----------------------------------------------- tablas por version (nivel M) */

/** [total de codewords, codewords de EC por bloque, bloques grupo 1, bloques grupo 2] */
const EC_M: Record<number, [number, number, number, number]> = {
    1: [26, 10, 1, 0],
    2: [44, 16, 1, 0],
    3: [70, 26, 1, 0],
    4: [100, 18, 2, 0],
    5: [134, 24, 2, 0],
    6: [172, 16, 4, 0],
    7: [196, 18, 4, 0],
    8: [242, 22, 2, 2],
    9: [292, 22, 3, 2],
    10: [346, 26, 4, 1],
    11: [404, 30, 1, 4],
    12: [466, 22, 6, 2],
    13: [532, 22, 8, 1],
    14: [581, 24, 4, 5],
    15: [655, 24, 5, 5],
    16: [733, 28, 7, 3],
    17: [815, 28, 10, 1],
    18: [901, 26, 9, 4],
    19: [991, 26, 3, 11],
    20: [1085, 26, 3, 13],
};

/** Posiciones centrales de los patrones de alineacion, por version. */
const ALIGN_POS: Record<number, number[]> = {
    1: [],
    2: [6, 18],
    3: [6, 22],
    4: [6, 26],
    5: [6, 30],
    6: [6, 34],
    7: [6, 22, 38],
    8: [6, 24, 42],
    9: [6, 26, 46],
    10: [6, 28, 50],
    11: [6, 30, 54],
    12: [6, 32, 58],
    13: [6, 34, 62],
    14: [6, 26, 46, 66],
    15: [6, 26, 48, 70],
    16: [6, 26, 50, 74],
    17: [6, 30, 54, 78],
    18: [6, 30, 56, 82],
    19: [6, 30, 58, 86],
    20: [6, 34, 62, 90],
};

const totalDataCodewords = (version: number): number => {
    const [total, ecPerBlock, g1, g2] = EC_M[version];
    return total - ecPerBlock * (g1 + g2);
};

/* --------------------------------------------------------------- bit stream */

class BitBuffer {
    bits: number[] = [];
    put(value: number, length: number) {
        for (let i = length - 1; i >= 0; i--) this.bits.push((value >>> i) & 1);
    }
}

/* ------------------------------------------------------ construccion del QR */

interface Grid {
    size: number;
    modules: (boolean | null)[][];
    reserved: boolean[][];
}

const newGrid = (version: number): Grid => {
    const size = version * 4 + 17;
    return {
        size,
        modules: Array.from({ length: size }, () => new Array<boolean | null>(size).fill(null)),
        reserved: Array.from({ length: size }, () => new Array<boolean>(size).fill(false)),
    };
};

const setModule = (g: Grid, r: number, c: number, dark: boolean) => {
    g.modules[r][c] = dark;
    g.reserved[r][c] = true;
};

const placeFinder = (g: Grid, row: number, col: number) => {
    // Patron 7x7 mas el separador blanco de 1 modulo alrededor.
    for (let r = -1; r <= 7; r++) {
        for (let c = -1; c <= 7; c++) {
            const rr = row + r;
            const cc = col + c;
            if (rr < 0 || rr >= g.size || cc < 0 || cc >= g.size) continue;
            const inRing = (r === 0 || r === 6) && c >= 0 && c <= 6;
            const inSide = (c === 0 || c === 6) && r >= 0 && r <= 6;
            const inCore = r >= 2 && r <= 4 && c >= 2 && c <= 4;
            setModule(g, rr, cc, inRing || inSide || inCore);
        }
    }
};

const placeAlignment = (g: Grid, version: number) => {
    const positions = ALIGN_POS[version];
    for (const r of positions) {
        for (const c of positions) {
            // Los tres vertices ya los ocupan los patrones de busqueda.
            if (g.reserved[r][c]) continue;
            for (let dr = -2; dr <= 2; dr++) {
                for (let dc = -2; dc <= 2; dc++) {
                    const ring = Math.max(Math.abs(dr), Math.abs(dc));
                    setModule(g, r + dr, c + dc, ring !== 1);
                }
            }
        }
    }
};

const placeTiming = (g: Grid) => {
    for (let i = 8; i < g.size - 8; i++) {
        const dark = i % 2 === 0;
        if (!g.reserved[6][i]) setModule(g, 6, i, dark);
        if (!g.reserved[i][6]) setModule(g, i, 6, dark);
    }
};

/** Reserva las zonas de formato y version (se rellenan despues de enmascarar). */
const reserveFormatAreas = (g: Grid, version: number) => {
    for (let i = 0; i < 9; i++) {
        if (!g.reserved[8][i]) setModule(g, 8, i, false);
        if (!g.reserved[i][8]) setModule(g, i, 8, false);
    }
    for (let i = 0; i < 8; i++) {
        if (!g.reserved[8][g.size - 1 - i]) setModule(g, 8, g.size - 1 - i, false);
        if (!g.reserved[g.size - 1 - i][8]) setModule(g, g.size - 1 - i, 8, false);
    }
    setModule(g, g.size - 8, 8, true); // modulo oscuro fijo

    if (version >= 7) {
        for (let i = 0; i < 6; i++) {
            for (let j = 0; j < 3; j++) {
                setModule(g, g.size - 11 + j, i, false);
                setModule(g, i, g.size - 11 + j, false);
            }
        }
    }
};

const MASKS: ((r: number, c: number) => boolean)[] = [
    (r, c) => (r + c) % 2 === 0,
    (r) => r % 2 === 0,
    (_r, c) => c % 3 === 0,
    (r, c) => (r + c) % 3 === 0,
    (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
    (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
    (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
    (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

/** Recorrido en zigzag de dos columnas, de abajo a la derecha hacia arriba. */
const placeData = (g: Grid, bits: number[], mask: (r: number, c: number) => boolean) => {
    let index = 0;
    let upward = true;

    for (let right = g.size - 1; right > 0; right -= 2) {
        if (right === 6) right--; // la columna 6 es de timing: se saltea
        const rows = upward
            ? Array.from({ length: g.size }, (_, i) => g.size - 1 - i)
            : Array.from({ length: g.size }, (_, i) => i);

        for (const r of rows) {
            for (const c of [right, right - 1]) {
                if (g.reserved[r][c]) continue;
                const bit = index < bits.length ? bits[index] === 1 : false;
                index++;
                g.modules[r][c] = mask(r, c) ? !bit : bit;
            }
        }
        upward = !upward;
    }
};

const FORMAT_MASK = 0x5412;

/** Bits de formato: nivel M (0b00) + numero de mascara, con BCH(15,5). */
const formatBits = (maskIndex: number): number => {
    const data = (0b00 << 3) | maskIndex;
    let value = data << 10;
    for (let i = 4; i >= 0; i--) {
        if ((value >>> (10 + i)) & 1) value ^= 0x537 << i;
    }
    return ((data << 10) | value) ^ FORMAT_MASK;
};

const versionBits = (version: number): number => {
    let value = version << 12;
    for (let i = 5; i >= 0; i--) {
        if ((value >>> (12 + i)) & 1) value ^= 0x1f25 << i;
    }
    return (version << 12) | value;
};

const applyFormatInfo = (g: Grid, maskIndex: number, version: number) => {
    const bits = formatBits(maskIndex);
    // Los 15 bits se colocan de mas significativo a menos: i=0 es el bit 14.
    const bitAt = (i: number) => ((bits >>> (14 - i)) & 1) === 1;

    // Copia junto al patron superior izquierdo.
    for (let i = 0; i <= 5; i++) g.modules[8][i] = bitAt(i);
    g.modules[8][7] = bitAt(6);
    g.modules[8][8] = bitAt(7);
    g.modules[7][8] = bitAt(8);
    for (let i = 9; i < 15; i++) g.modules[14 - i][8] = bitAt(i);

    // Copia redundante repartida entre los otros dos patrones.
    for (let i = 0; i < 8; i++) g.modules[g.size - 1 - i][8] = bitAt(i);
    for (let i = 8; i < 15; i++) g.modules[8][g.size - 15 + i] = bitAt(i);
    g.modules[g.size - 8][8] = true;

    if (version >= 7) {
        const vBits = versionBits(version);
        for (let i = 0; i < 18; i++) {
            const dark = ((vBits >>> i) & 1) === 1;
            const r = Math.floor(i / 3);
            const c = (i % 3) + g.size - 11;
            g.modules[r][c] = dark;
            g.modules[c][r] = dark;
        }
    }
};

/** Penalizacion estandar (reglas N1-N4) para elegir la mascara. */
const penalty = (g: Grid): number => {
    const size = g.size;
    const at = (r: number, c: number) => g.modules[r][c] === true;
    let score = 0;

    // N1: corridas de 5 o mas del mismo color, por fila y por columna.
    for (let i = 0; i < size; i++) {
        for (const horizontal of [true, false]) {
            let run = 1;
            for (let j = 1; j < size; j++) {
                const prev = horizontal ? at(i, j - 1) : at(j - 1, i);
                const curr = horizontal ? at(i, j) : at(j, i);
                if (curr === prev) {
                    run++;
                } else {
                    if (run >= 5) score += 3 + (run - 5);
                    run = 1;
                }
            }
            if (run >= 5) score += 3 + (run - 5);
        }
    }

    // N2: bloques 2x2 monocromos.
    for (let r = 0; r < size - 1; r++) {
        for (let c = 0; c < size - 1; c++) {
            const v = at(r, c);
            if (v === at(r, c + 1) && v === at(r + 1, c) && v === at(r + 1, c + 1)) score += 3;
        }
    }

    // N3: patron 1:1:3:1:1 con zona clara, que se confunde con un finder.
    const pattern = [true, false, true, true, true, false, true];
    const matches = (get: (k: number) => boolean, start: number) => {
        for (let k = 0; k < 7; k++) if (get(start + k) !== pattern[k]) return false;
        return true;
    };
    const quietBefore = (get: (k: number) => boolean, start: number) => {
        for (let k = start - 4; k < start; k++) if (k < 0 || get(k)) return false;
        return true;
    };
    const quietAfter = (get: (k: number) => boolean, start: number) => {
        for (let k = start + 7; k < start + 11; k++) if (k >= size || get(k)) return false;
        return true;
    };
    for (let i = 0; i < size; i++) {
        for (const horizontal of [true, false]) {
            const get = (k: number) => (horizontal ? at(i, k) : at(k, i));
            for (let j = 0; j + 7 <= size; j++) {
                if (matches(get, j) && (quietBefore(get, j) || quietAfter(get, j))) score += 40;
            }
        }
    }

    // N4: desvio respecto del 50% de modulos oscuros.
    let dark = 0;
    for (let r = 0; r < size; r++) for (let c = 0; c < size; c++) if (at(r, c)) dark++;
    const ratio = (dark * 100) / (size * size);
    score += Math.floor(Math.abs(ratio - 50) / 5) * 10;

    return score;
};

/* --------------------------------------------------------------- bloques EC */

/** Intercala bloques de datos y de correccion segun manda la norma. */
const buildCodewords = (data: Uint8Array, version: number): Uint8Array => {
    const [, ecPerBlock, g1Count, g2Count] = EC_M[version];
    const totalBlocks = g1Count + g2Count;
    const g1Size = Math.floor(data.length / totalBlocks);
    const g2Size = g1Size + 1;

    const dataBlocks: Uint8Array[] = [];
    const ecBlocks: Uint8Array[] = [];
    let offset = 0;
    for (let i = 0; i < totalBlocks; i++) {
        const size = i < g1Count ? g1Size : g2Size;
        const block = data.slice(offset, offset + size);
        offset += size;
        dataBlocks.push(block);
        ecBlocks.push(rsEncode(block, ecPerBlock));
    }

    const out: number[] = [];
    for (let i = 0; i < g2Size; i++) {
        for (const block of dataBlocks) if (i < block.length) out.push(block[i]);
    }
    for (let i = 0; i < ecPerBlock; i++) {
        for (const block of ecBlocks) out.push(block[i]);
    }
    return new Uint8Array(out);
};

const PAD_BYTES = [0xec, 0x11];

/**
 * Codifica `text` (UTF-8, modo byte) y devuelve la matriz de modulos.
 * Lanza si el texto no entra en las versiones soportadas.
 */
export const generateQrMatrix = (text: string): boolean[][] => {
    const bytes = new TextEncoder().encode(text);

    let version = 0;
    for (let v = 1; v <= 20; v++) {
        const lengthBits = v < 10 ? 8 : 16;
        const capacityBits = totalDataCodewords(v) * 8;
        if (4 + lengthBits + bytes.length * 8 <= capacityBits) {
            version = v;
            break;
        }
    }
    if (!version) throw new Error('QR: contenido demasiado largo');

    const buffer = new BitBuffer();
    buffer.put(0b0100, 4); // indicador de modo byte
    buffer.put(bytes.length, version < 10 ? 8 : 16);
    for (let i = 0; i < bytes.length; i++) buffer.put(bytes[i], 8);

    const capacityBits = totalDataCodewords(version) * 8;
    buffer.put(0, Math.min(4, capacityBits - buffer.bits.length)); // terminador
    while (buffer.bits.length % 8 !== 0) buffer.bits.push(0);

    const data = new Uint8Array(totalDataCodewords(version));
    for (let i = 0; i < buffer.bits.length; i += 8) {
        let byte = 0;
        for (let j = 0; j < 8; j++) byte = (byte << 1) | buffer.bits[i + j];
        data[i / 8] = byte;
    }
    for (let i = buffer.bits.length / 8, p = 0; i < data.length; i++, p++) {
        data[i] = PAD_BYTES[p % 2];
    }

    const codewords = buildCodewords(data, version);
    const bits: number[] = [];
    for (let k = 0; k < codewords.length; k++) {
        for (let i = 7; i >= 0; i--) bits.push((codewords[k] >>> i) & 1);
    }

    // Se prueban las 8 mascaras y se conserva la de menor penalizacion.
    let best: Grid | null = null;
    let bestScore = Infinity;
    for (let m = 0; m < 8; m++) {
        const grid = newGrid(version);
        placeFinder(grid, 0, 0);
        placeFinder(grid, 0, grid.size - 7);
        placeFinder(grid, grid.size - 7, 0);
        placeAlignment(grid, version);
        placeTiming(grid);
        reserveFormatAreas(grid, version);
        placeData(grid, bits, MASKS[m]);
        applyFormatInfo(grid, m, version);

        const score = penalty(grid);
        if (score < bestScore) {
            bestScore = score;
            best = grid;
        }
    }

    return best!.modules.map(row => row.map(cell => cell === true));
};
